import { ethers } from 'ethers';
import {
  BLOKISTA_NETWORK,
  CONTRACT_ADDRESSES,
  BCUSD_USD_PRICE,
  WBCC_USD_PRICE_FALLBACK,
} from './web3';

const factoryAbi = ['function getPair(address,address) view returns (address)'];
const pairAbi = [
  'function getReserves() view returns (uint112,uint112,uint32)',
  'function token0() view returns (address)',
];

let _rpc = null;
const getRpc = () => {
  if (!_rpc) {
    _rpc = new ethers.providers.JsonRpcProvider(BLOKISTA_NETWORK.rpcUrls[0]);
  }
  return _rpc;
};

// Internal cache for reserves: 30s TTL
const reservesCache = new Map(); // key: `${a}-${b}` -> { ts, value }
const RESERVES_TTL_MS = 30_000;

const reservesOf = async (a, b, decA, decB) => {
  const key = `${a.toLowerCase()}-${b.toLowerCase()}-${decA}-${decB}`;
  const cached = reservesCache.get(key);
  if (cached && Date.now() - cached.ts < RESERVES_TTL_MS) return cached.value;

  const rpc = getRpc();
  const factoryC = new ethers.Contract(CONTRACT_ADDRESSES.FACTORY, factoryAbi, rpc);
  const pair = await factoryC.getPair(a, b);
  if (!pair || pair === ethers.constants.AddressZero) {
    reservesCache.set(key, { ts: Date.now(), value: null });
    return null;
  }
  const pairC = new ethers.Contract(pair, pairAbi, rpc);
  const [r, t0] = await Promise.all([pairC.getReserves(), pairC.token0()]);
  const aIsToken0 = t0.toLowerCase() === a.toLowerCase();
  const reserveARaw = aIsToken0 ? r[0] : r[1];
  const reserveBRaw = aIsToken0 ? r[1] : r[0];
  if (reserveARaw.isZero() || reserveBRaw.isZero()) {
    reservesCache.set(key, { ts: Date.now(), value: null });
    return null;
  }
  const value = {
    reserveA: parseFloat(ethers.utils.formatUnits(reserveARaw, decA)),
    reserveB: parseFloat(ethers.utils.formatUnits(reserveBRaw, decB)),
  };
  reservesCache.set(key, { ts: Date.now(), value });
  return value;
};

/**
 * Compute a token's USD price from on-chain pool reserves.
 *  - BCUSD = $BCUSD_USD_PRICE
 *  - Native BCC / WBCC: routed via WBCC<->BCUSD pool
 *  - Anything else: direct token<->BCUSD or 2-hop token<->WBCC<->BCUSD
 * Returns 0 if no liquid path exists.
 */
export const computeTokenUsdPrice = async (token) => {
  if (!token) return 0;
  try {
    const tokenAddrRaw = token.isNative || token.is_native
      ? CONTRACT_ADDRESSES.WBCC
      : token.address;
    if (!tokenAddrRaw) return 0;
    const tokenAddr = tokenAddrRaw.toLowerCase();
    const bcusd = CONTRACT_ADDRESSES.BCUSD.toLowerCase();
    const wbcc = CONTRACT_ADDRESSES.WBCC.toLowerCase();
    const tokenDecimals = token.decimals || 18;

    if (tokenAddr === bcusd) return BCUSD_USD_PRICE;

    const direct = await reservesOf(tokenAddr, bcusd, tokenDecimals, 18);
    if (direct) return (direct.reserveB / direct.reserveA) * BCUSD_USD_PRICE;

    // WBCC price: prefer on-chain WBCC<->BCUSD, else fall back to constant
    if (tokenAddr === wbcc) {
      const wb = await reservesOf(wbcc, bcusd, 18, 18);
      if (wb) return (wb.reserveB / wb.reserveA) * BCUSD_USD_PRICE;
      return WBCC_USD_PRICE_FALLBACK;
    }

    // 2-hop route: token <-> WBCC <-> BCUSD (or fallback WBCC price)
    const tokToWbcc = await reservesOf(tokenAddr, wbcc, tokenDecimals, 18);
    if (!tokToWbcc) return 0;
    const wbccToBcusd = await reservesOf(wbcc, bcusd, 18, 18);
    const wbccInUsd = wbccToBcusd
      ? (wbccToBcusd.reserveB / wbccToBcusd.reserveA) * BCUSD_USD_PRICE
      : WBCC_USD_PRICE_FALLBACK;
    const tokenInWbcc = tokToWbcc.reserveB / tokToWbcc.reserveA;
    return tokenInWbcc * wbccInUsd;
  } catch (err) {
    console.error(`[prices] price lookup failed for ${token?.symbol || token?.address}:`, err);
    return 0;
  }
};

/**
 * Compute USD prices for an array of tokens in parallel.
 * Returns: { [address-or-id]: number }
 */
export const computeTokenPricesMap = async (tokens) => {
  const entries = await Promise.all(
    tokens.map(async (t) => {
      const price = await computeTokenUsdPrice(t);
      return [t.address?.toLowerCase() || t.id, price];
    })
  );
  return Object.fromEntries(entries);
};
